import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://murko-coms-api.azurewebsites.net";

const SOURCE_LABELS = {
  walk_in: "Walk-in",
  app: "App",
  meta_ad: "META Ad",
  qr_physical: "QR Physical",
  qr_promo: "QR Promo",
  qr_event: "QR Event",
  playpicklr_form: "PlayPicklr Form",
  activation_upload: "Activation Upload",
  pb101_form: "PB101 Form",
  trial_form: "Trial Form",
  retargeting_form: "Retargeting",
  annual_form: "Annual Form",
};

const SOURCE_COLORS = {
  meta_ad: "#1877F2",
  playpicklr_form: "#16a34a",
  activation_upload: "#7c3aed",
  pb101_form: "#0891b2",
  trial_form: "#0891b2",
  app: "#ea580c",
  qr_physical: "#78716c",
  qr_promo: "#78716c",
  qr_event: "#d97706",
  walk_in: "#6b7280",
  retargeting_form: "#be185d",
  annual_form: "#0f766e",
};

const MARKET_COLORS = {
  TX: { bg: "#dbeafe", text: "#1e40af" },
  WI: { bg: "#dcfce7", text: "#166534" },
};

const STATUS_TABS = ["new", "contacted", "promoted", "duplicate", "invalid"];

const OUTCOME_OPTIONS = [
  'Left Voicemail',
  'No Answer',
  'Text Sent',
  'Email Sent',
  'Spoke With — Interested',
  'Spoke With — Soft No',
  'Spoke With — Hard No',
  'Converted',
  'Re-Engage',
  'Called + Email',
];
const OUTCOMES_REQUIRING_WHY = ['Spoke With — Soft No', 'Spoke With — Hard No'];
const OUTCOMES_NO_FOLLOWUP   = ['Spoke With — Hard No', 'Converted'];

const CONTACT_METHODS = [
  { value: 'call',      label: '📞 Call' },
  { value: 'text',      label: '💬 Text' },
  { value: 'email',     label: '📧 Email' },
  { value: 'in-person', label: '🤝 In Person' },
];

const PIPELINE_OPTIONS = [
  { value: "pb101", label: "PB101 Session" },
  { value: "t2m", label: "Trial (T2M)" },
  { value: "lead_engine", label: "Lead Engine" },
];
// Events & Sponsorship sales (10/9/26). One lead can want both, e.g. "Enter to win a Free Private Event" AND "Register
// for a Free Learn to Play class", so promotion has two independent halves: a PLAYING path (PB101 / Trial / Lead Engine,
// member-side) and an EVENT or SPONSORSHIP deal (a business deal in Events & Sponsorship sales). Either, both, or one
// now and the other later. E&S deals are never offered as a re-route target.
const ES_TYPES = [
  { value: "event", label: "Event" },
  { value: "sponsorship", label: "Sponsorship" },
];
const ALL_DESTINATIONS = [
  ...PIPELINE_OPTIONS,
  { value: "es_event", label: "Event (E&S)" },
  { value: "es_sponsorship", label: "Sponsorship (E&S)" },
];
const ES_CLUBS = ["DEN", "LEW", "MCK", "GB", "MF", "WA"];
const MEMBER_PATHS = PIPELINE_OPTIONS.map(o => o.value);

// Every form answer as { key, value }. The notes line ("key: value | key: value") holds the full set; custom_responses
// can be incomplete on older leads (multi-select used to keep only the last pick), so both are read.
function formAnswers(lead) {
  const out = [];
  for (const part of String(lead.notes || "").split(/\s\|\s|\n/)) {
    const i = part.indexOf(":");
    if (i <= 0 || /^dismissed/i.test(part.trim())) continue;
    const value = part.slice(i + 1).trim();
    if (value) out.push({ key: part.slice(0, i).trim(), value });
  }
  const cr = lead.custom_responses;
  if (cr && typeof cr === "object") {
    for (const [key, v] of Object.entries(cr)) {
      for (const value of String(v || "").split(/,\s(?=[A-Z])/)) if (value.trim()) out.push({ key, value: value.trim() });
    }
  }
  return out;
}

// Suggestions from the answers. Staff confirm or change them; nothing routes automatically (auto-routing is backlog).
function suggestPromotion(lead) {
  const answers = formAnswers(lead);
  const hit = re => answers.filter(a => re.test(a.value) || re.test(a.key));
  const why = list => [...new Set(list.map(a => a.value))].join("; ");

  let es = null, esWhy = "";
  const sponsor = hit(/sponsor/i);
  const event = hit(/private event|team[- ]?building|corporate event|company event|outing|party|book a private event/i);
  if (sponsor.length) { es = "sponsorship"; esWhy = why(sponsor); }
  else if (event.length) { es = "event"; esWhy = why(event); }

  let playing = null, playingWhy = "";
  const rules = [
    ["lead_engine", /ready.*membership|want a membership|which[ _]plan|discount.*membership/i],
    ["pb101", /learn to play|pickleball 101|\b101\b|clinic/i],
    ["t2m", /trial/i],
    ["lead_engine", /membership/i],
  ];
  for (const [path, re] of rules) {
    const h = hit(re);
    if (h.length) { playing = path; playingWhy = why(h); break; }
  }

  let company = "";
  for (const a of answers) {
    if (/business_name|company|organi[sz]ation|employer/i.test(a.key) && !/industry|website|size/i.test(a.key)) { company = a.value; break; }
  }
  return { playing, playingWhy, es, esWhy, company };
}

const QUICK_PICKS = [
  { label: 'Today',     days: 0 },
  { label: 'Tomorrow',  days: 1 },
  { label: 'In 2 days', days: 2 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
];

const TIME_OPTIONS = [
  '8:00 AM','8:30 AM','9:00 AM','9:30 AM','10:00 AM','10:30 AM',
  '11:00 AM','11:30 AM','12:00 PM','12:30 PM','1:00 PM','1:30 PM',
  '2:00 PM','2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM',
  '5:00 PM','5:30 PM','6:00 PM','6:30 PM','7:00 PM','7:30 PM','8:00 PM',
];

function addDays(d, n) {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}
function toISO(date) { return date.toISOString().split('T')[0]; }
function formatDateDisplay(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}


function formatDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function KpiBar({ overview }) {
  if (!overview) return <div style={styles.kpiBar}><span style={styles.kpiLoading}>Loading KPIs...</span></div>;

  const { total, new_today, by_market } = overview;

  return (
    <div style={styles.kpiBar}>
      <div style={styles.kpiGroup}>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{new_today?.total ?? 0}</div>
          <div style={styles.kpiLabel}>New Today</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{total?.new ?? 0}</div>
          <div style={styles.kpiLabel}>Total New</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{total?.contacted ?? 0}</div>
          <div style={styles.kpiLabel}>Contacted</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{total?.promoted ?? 0}</div>
          <div style={styles.kpiLabel}>Promoted</div>
        </div>
      </div>
      <div style={styles.kpiMarkets}>
        {["TX", "WI"].map((m) => {
          const d = by_market?.[m] || {};
          return (
            <div key={m} style={{ ...styles.kpiMarketChip, background: MARKET_COLORS[m].bg }}>
              <span style={{ ...styles.kpiMarketLabel, color: MARKET_COLORS[m].text }}>{m}</span>
              <span style={styles.kpiMarketStat}>
                {d.new_today ?? 0} today · {d.new ?? 0} new · {d.contacted ?? 0} contacted · {d.promoted ?? 0} promoted
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SourceBadge({ source }) {
  const label = SOURCE_LABELS[source] || source;
  const color = SOURCE_COLORS[source] || "#6b7280";
  return (
    <span style={{ ...styles.badge, background: color + "22", color, border: `1px solid ${color}55` }}>
      {label}
    </span>
  );
}

function MarketBadge({ market }) {
  if (!market) return null;
  const c = MARKET_COLORS[market] || { bg: "#f3f4f6", text: "#374151" };
  return (
    <span style={{ ...styles.badge, background: c.bg, color: c.text }}>
      {market}
    </span>
  );
}

function ClubBadge({ locationId, locationName }) {
  return (
    <span style={{ ...styles.badge, background: "#f3f4f6", color: "#374151" }}>
      {locationName || locationId || "—"}
    </span>
  );
}

function ContactModal({ lead, onClose, onSaved, token }) {
  const [outcome, setOutcome]             = useState('');
  const [contactMethod, setContactMethod] = useState('');
  const [why, setWhy]                     = useState('');
  const [notes, setNotes]                 = useState('');
  const [scheduleFollowUp, setScheduleFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate]   = useState('');
  const [followUpTime, setFollowUpTime]   = useState('9:00 AM');
  const [followUpNote, setFollowUpNote]   = useState('');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState(null);

  const needsWhy   = OUTCOMES_REQUIRING_WHY.includes(outcome);
  const noFollowUp = OUTCOMES_NO_FOLLOWUP.includes(outcome);

  useEffect(() => {
    if (noFollowUp) setScheduleFollowUp(false);
  }, [outcome, noFollowUp]);

  function selectQuick(pick) {
    const date = toISO(addDays(new Date(), pick.days));
    setFollowUpDate(date);
    setScheduleFollowUp(true);
  }

  async function handleSave() {
    if (!outcome)       { setError('Select an outcome'); return; }
    if (!contactMethod) { setError('Select a contact method'); return; }
    if (needsWhy && !why.trim()) { setError('Please enter a reason'); return; }
    if (scheduleFollowUp && !followUpDate) { setError('Select a follow-up date'); return; }

    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/inbound/leads/${lead.lead_id}/outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          outcome,
          contactMethod,
          notes: why ? `${notes}\nReason: ${why}`.trim() : notes,
          followUpDate:  scheduleFollowUp ? followUpDate  : null,
          followUpTime:  scheduleFollowUp ? followUpTime  : null,
          followUpNote:  scheduleFollowUp ? followUpNote  : null,
        }),
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.error || 'Save failed'); }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const m = { background: '#0d1117', border: '1px solid #222', borderRadius: 10, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', fontFamily: 'DM Sans, Inter, system-ui, sans-serif' };
  const mh = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #1a1a1a' };
  const mb = { padding: '20px' };
  const mf = { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid #1a1a1a' };
  const lbl = { fontSize: 10, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' };
  const fg = { marginBottom: 14 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={m}>
        <div style={mh}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#e5e5e5' }}>
            📝 Log Contact — <span style={{ color: '#c084fc' }}>{lead.first_name} {lead.last_name}</span>
          </span>
          <button style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#666', padding: 4 }} onClick={onClose}>✕</button>
        </div>

        <div style={mb}>
          {/* Contact info line */}
          <div style={{ fontSize: 11, color: '#555', marginBottom: 16 }}>
            {lead.email}{lead.phone ? ` · ${lead.phone}` : ''}{lead.location_name ? ` · ${lead.location_name}` : ''}
          </div>

          {/* Contact Method */}
          <div style={fg}>
            <span style={{ ...lbl, color: '#ef4444' }}>Contact Method <span style={{ color: '#ef4444' }}>*</span></span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CONTACT_METHODS.map(cm => (
                <button key={cm.value} onClick={() => setContactMethod(cm.value)} style={{
                  fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                  border: `1px solid ${contactMethod === cm.value ? '#facc15' : '#333'}`,
                  background: contactMethod === cm.value ? '#facc1515' : '#111',
                  color: contactMethod === cm.value ? '#facc15' : '#888',
                  fontWeight: contactMethod === cm.value ? 700 : 400,
                }}>
                  {cm.label}
                </button>
              ))}
            </div>
          </div>

          {/* Outcome pills */}
          <div style={fg}>
            <span style={lbl}>Outcome</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {OUTCOME_OPTIONS.map(o => (
                <button key={o} onClick={() => setOutcome(o)} style={{
                  fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                  border: `1px solid ${outcome === o ? '#22c55e' : '#333'}`,
                  background: outcome === o ? '#22c55e15' : '#111',
                  color: outcome === o ? '#22c55e' : '#888',
                  fontWeight: outcome === o ? 700 : 400,
                }}>
                  {o}
                </button>
              ))}
            </div>
          </div>

          {/* Why — required for Soft No / Hard No */}
          {needsWhy && (
            <div style={fg}>
              <span style={{ ...lbl, color: '#ef4444' }}>Why? (required)</span>
              <input
                type="text"
                value={why}
                onChange={e => setWhy(e.target.value)}
                placeholder={outcome.includes('Soft') ? 'e.g. Price, timing, not ready...' : 'e.g. Not interested, DNC...'}
                style={{ width: '100%', background: '#111', border: '1px solid #ef444466', color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12, boxSizing: 'border-box' }}
              />
            </div>
          )}

          {/* Notes */}
          <div style={fg}>
            <span style={lbl}>Notes (optional)</span>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Additional context for next rep..."
              rows={2}
              style={{ width: '100%', background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12, resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
          </div>

          {/* Follow-up scheduler */}
          {!noFollowUp && outcome && (
            <div style={fg}>
              <div onClick={() => setScheduleFollowUp(p => !p)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: scheduleFollowUp ? 10 : 0 }}>
                <div style={{ width: 36, height: 20, borderRadius: 10, background: scheduleFollowUp ? '#facc15' : '#333', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: scheduleFollowUp ? '#000' : '#666', position: 'absolute', top: 2, left: scheduleFollowUp ? 18 : 2, transition: 'left 0.2s' }} />
                </div>
                <span style={{ fontSize: 13, color: scheduleFollowUp ? '#facc15' : '#888' }}>Schedule a follow-up</span>
              </div>

              {scheduleFollowUp && (
                <div style={{ background: '#0a0f1a', border: '1px solid #facc1530', borderRadius: 8, padding: '12px 14px' }}>
                  {/* Quick picks */}
                  <div style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Quick select</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {QUICK_PICKS.map(p => (
                      <button key={p.label} onClick={() => selectQuick(p)} style={{
                        fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                        border: `1px solid ${followUpDate === toISO(addDays(new Date(), p.days)) ? '#facc15' : '#333'}`,
                        background: followUpDate === toISO(addDays(new Date(), p.days)) ? '#facc1515' : '#111',
                        color: followUpDate === toISO(addDays(new Date(), p.days)) ? '#facc15' : '#888',
                      }}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {/* Date + Time row */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input
                      type="date"
                      value={followUpDate}
                      min={toISO(new Date())}
                      onChange={e => setFollowUpDate(e.target.value)}
                      style={{ flex: 1, background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12 }}
                    />
                    <select
                      value={followUpTime}
                      onChange={e => setFollowUpTime(e.target.value)}
                      style={{ flex: 1, background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12 }}
                    >
                      {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  {followUpDate && (
                    <div style={{ fontSize: 11, color: '#facc15', marginBottom: 8 }}>
                      📅 {formatDateDisplay(followUpDate)} at {followUpTime}
                    </div>
                  )}
                  <input
                    type="text"
                    value={followUpNote}
                    onChange={e => setFollowUpNote(e.target.value)}
                    placeholder="Follow-up note (optional)..."
                    style={{ width: '100%', background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12, boxSizing: 'border-box' }}
                  />
                </div>
              )}
            </div>
          )}

          {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{error}</div>}
        </div>

        <div style={mf}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: '8px 18px', background: 'transparent', color: '#888', border: '1px solid #333', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !outcome || !contactMethod}
            style={{ padding: '8px 18px', background: saving || !outcome || !contactMethod ? '#333' : '#facc15', color: saving || !outcome || !contactMethod ? '#555' : '#000', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: saving || !outcome || !contactMethod ? 'not-allowed' : 'pointer', minWidth: 160 }}
          >
            {saving ? 'Saving...' : scheduleFollowUp ? '📅 Save + Schedule Follow-Up' : '✓ Save Contact'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PromoteModal({ lead, onClose, onSaved, token }) {
  const sug = suggestPromotion(lead);
  const hasPlaying = MEMBER_PATHS.includes(lead.promoted_to_pipeline);
  const esDone = (lead.es_deals || []).map(d => d.deal_type);
  const esChoices = ES_TYPES.filter(t => !esDone.includes(t.value));

  const [playing, setPlaying] = useState(hasPlaying ? "" : (sug.playing || ""));
  const [esType, setEsType] = useState(sug.es && !esDone.includes(sug.es) ? sug.es : "");
  const [es, setEs] = useState({
    company: sug.company, location_id: lead.location_id || "", priority: "medium", value: "", event_date: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handlePromote() {
    setError(null);
    if (!playing && !esType) { setError("Pick a membership path, an event/sponsorship, or both."); return; }
    if (esType === "sponsorship" && !es.company.trim()) { setError("Company name is required for a sponsorship."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/inbound/leads/${lead.lead_id}/promote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          playing: playing || null,
          es: esType ? { type: esType, ...es, location_id: es.location_id || null } : null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Promote failed");
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to promote.");
    } finally {
      setSaving(false);
    }
  }

  const section = { border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", marginBottom: 12 };
  const sectionTitle = { fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 };
  const why = { fontSize: 12, color: "#6b7280", margin: "8px 0 0" };
  const option = (selected) => ({ ...styles.pipelineOption, ...(selected ? styles.pipelineOptionSelected : {}), padding: "10px" });

  return (
    <div style={styles.modalOverlay}>
      <div style={{ ...styles.modal, maxWidth: 560 }}>
        <div style={styles.modalHeader}>
          <span style={styles.modalTitle}>Promote: {lead.first_name} {lead.last_name}</span>
          <button style={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={styles.modalBody}>
          <p style={styles.modalHint}>
            A lead can go to a membership path, an event or sponsorship deal, or both. Suggestions come from their form
            answers; change them if they are wrong.
          </p>

          <div style={section}>
            <div style={sectionTitle}>1 · Membership</div>
            {hasPlaying ? (
              <div style={{ fontSize: 13, color: "#15803d", fontWeight: 600 }}>
                Already on {ALL_DESTINATIONS.find(p => p.value === lead.promoted_to_pipeline)?.label}. Use Re-route to change it.
              </div>
            ) : (
              <>
                <div style={{ ...styles.pipelineOptions, flexWrap: "wrap" }}>
                  {[{ value: "", label: "None" }, ...PIPELINE_OPTIONS].map(o => (
                    <label key={o.value || "none"} style={option(playing === o.value)}>
                      <input type="radio" name="playing" checked={playing === o.value} onChange={() => setPlaying(o.value)} style={{ display: "none" }} />
                      <span style={styles.pipelineOptionLabel}>{o.label}</span>
                    </label>
                  ))}
                </div>
                {sug.playing && <p style={why}>Suggested from: {sug.playingWhy}</p>}
              </>
            )}
          </div>

          <div style={section}>
            <div style={sectionTitle}>2 · Event or sponsorship (Events &amp; Sponsorship sales)</div>
            {esDone.length > 0 && (
              <div style={{ fontSize: 13, color: "#15803d", fontWeight: 600, marginBottom: 8 }}>
                Already has: {(lead.es_deals || []).map(d => `${d.deal_type === "event" ? "Event" : "Sponsorship"} (${d.name})`).join(", ")}
              </div>
            )}
            {esChoices.length > 0 && (
              <div style={{ ...styles.pipelineOptions, flexWrap: "wrap" }}>
                {[{ value: "", label: "None" }, ...esChoices].map(o => (
                  <label key={o.value || "none"} style={option(esType === o.value)}>
                    <input type="radio" name="es" checked={esType === o.value} onChange={() => setEsType(o.value)} style={{ display: "none" }} />
                    <span style={styles.pipelineOptionLabel}>{o.label}</span>
                  </label>
                ))}
              </div>
            )}
            {sug.es && !esDone.includes(sug.es) && <p style={why}>Suggested from: {sug.esWhy}</p>}
            {esType && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
                <div style={{ gridColumn: "span 2" }}>
                  <div style={styles.label}>
                    {esType === "event" ? "Company or group (leave blank for a private/personal event)" : "Company"}
                  </div>
                  <input style={styles.input} value={es.company} onChange={e => setEs({ ...es, company: e.target.value })} />
                </div>
                <div>
                  <div style={styles.label}>Club</div>
                  <select style={styles.input} value={es.location_id} onChange={e => setEs({ ...es, location_id: e.target.value })}>
                    <option value="">Multi-club / not set</option>
                    {ES_CLUBS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <div style={styles.label}>Priority</div>
                  <select style={styles.input} value={es.priority} onChange={e => setEs({ ...es, priority: e.target.value })}>
                    <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <div style={styles.label}>{esType === "event" ? "Price ($)" : "Value per year ($)"}</div>
                  <input style={styles.input} value={es.value} onChange={e => setEs({ ...es, value: e.target.value })} />
                </div>
                {esType === "event" && (
                  <div>
                    <div style={styles.label}>Event date (if known)</div>
                    <input type="date" style={styles.input} value={es.event_date} onChange={e => setEs({ ...es, event_date: e.target.value })} />
                  </div>
                )}
              </div>
            )}
          </div>

          {error && <div style={styles.errorMsg}>{error}</div>}
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={styles.btnPrimary} onClick={handlePromote} disabled={saving || (!playing && !esType)}>
            {saving ? "Promoting..." : playing && esType ? "Promote to both" : "Promote"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RerouteModal({ lead, onClose, onSaved, token }) {
  const [pipeline, setPipeline] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const currentLabel = ALL_DESTINATIONS.find(p => p.value === lead.promoted_to_pipeline)?.label || lead.promoted_to_pipeline;

  async function handleReroute() {
    if (!pipeline) { setError("Select a pipeline."); return; }
    if (pipeline === lead.promoted_to_pipeline) { setError("That's the same pipeline — pick a different one."); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/inbound/leads/${lead.lead_id}/reroute`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pipeline }),
      });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || "Re-route failed");
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to re-route.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <span style={styles.modalTitle}>Re-route — {lead.first_name} {lead.last_name}</span>
          <button style={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={styles.modalBody}>
          <p style={styles.modalHint}>
            Currently routed to <strong>{currentLabel}</strong>. Select the correct pipeline to move this lead.
          </p>
          <div style={styles.pipelineOptions}>
            {PIPELINE_OPTIONS.filter(o => o.value !== lead.promoted_to_pipeline).map(o => (
              <label key={o.value} style={{
                ...styles.pipelineOption,
                ...(pipeline === o.value ? styles.pipelineOptionSelected : {}),
              }}>
                <input
                  type="radio"
                  name="reroute_pipeline"
                  value={o.value}
                  checked={pipeline === o.value}
                  onChange={() => setPipeline(o.value)}
                  style={{ display: "none" }}
                />
                <span style={styles.pipelineOptionLabel}>{o.label}</span>
              </label>
            ))}
          </div>
          {error && <div style={styles.errorMsg}>{error}</div>}
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...styles.btnPrimary, background: "#7c3aed" }} onClick={handleReroute} disabled={saving || !pipeline}>
            {saving ? "Moving..." : "Re-route"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DismissModal({ lead, onClose, onSaved, token }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleDismiss() {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/api/inbound/leads/${lead.lead_id}/dismiss`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason }),
      });
      onSaved();
      onClose();
    } catch (e) {
      // silent
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <span style={styles.modalTitle}>Dismiss — {lead.first_name} {lead.last_name}</span>
          <button style={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Reason (optional)</label>
            <input
              style={styles.input}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Wrong number, spam, duplicate..."
            />
          </div>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...styles.btnPrimary, background: "#dc2626" }} onClick={handleDismiss} disabled={saving}>
            {saving ? "Dismissing..." : "Mark Invalid"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadCard({ lead, onContact, onPromote, onDismiss, onReroute }) {
  const isMatched = !!lead.member_id_matched;

  return (
    <div style={styles.card}>
      <div style={styles.cardTop}>
        <div style={styles.cardName}>
          {lead.first_name} {lead.last_name}
          {isMatched && (
            <span style={styles.matchBadge} title="Matched to existing member">● Member</span>
          )}
        </div>
        <div style={styles.cardBadges}>
          <SourceBadge source={lead.source} />
          <MarketBadge market={lead.market} />
          <ClubBadge locationId={lead.location_id} locationName={lead.location_name} />
          {lead.activation_name && (
            <span style={{ ...styles.badge, background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>
              ⚡ {lead.activation_name}
            </span>
          )}
          {/* sms_consent: false = guest declined texts (optional consent on
              event waiver forms); null = older lead, consent was required */}
          {lead.sms_consent === false && (
            <span title="Declined recurring automated texts on the form. Personal follow-up by phone or email is fine; keep them out of automated/bulk texts."
              style={{ ...styles.badge, background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}>
              🚫 No auto-texts
            </span>
          )}
        </div>
      </div>

      <div style={styles.cardContact}>
        {lead.email && <span style={styles.cardDetail}>✉ {lead.email}</span>}
        {lead.phone && <span style={styles.cardDetail}>📱 {lead.phone}</span>}
      </div>

      {(lead.utm_source || lead.utm_campaign) && (
        <div style={styles.cardUtm}>
          {lead.utm_source && <span style={styles.utmTag}>src: {lead.utm_source}</span>}
          {lead.utm_campaign && <span style={styles.utmTag}>cmp: {lead.utm_campaign}</span>}
        </div>
      )}

      {lead.notes && (
        <div style={styles.cardNotes}>{lead.notes}</div>
      )}

      <div style={styles.cardFooter}>
        <span style={styles.cardTimestamp}>{formatDate(lead.created_at)}</span>
        {lead.status !== "promoted" && lead.status !== "invalid" && (
          <div style={styles.cardActions}>
            <button style={styles.btnAction} onClick={() => onContact(lead)}>Log Contact</button>
            <button style={{ ...styles.btnAction, ...styles.btnActionPromote }} onClick={() => onPromote(lead)}>
              Promote ▸
            </button>
            <button style={{ ...styles.btnAction, ...styles.btnActionDismiss }} onClick={() => onDismiss(lead)}>
              Dismiss
            </button>
          </div>
        )}
        {lead.status === "promoted" && (() => {
          const playingPath = MEMBER_PATHS.includes(lead.promoted_to_pipeline) ? lead.promoted_to_pipeline : null;
          const deals = lead.es_deals || [];
          const canAdd = !playingPath || deals.length < ES_TYPES.length;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {playingPath && (
                <span style={styles.promotedTag}>→ {PIPELINE_OPTIONS.find(p => p.value === playingPath)?.label}</span>
              )}
              {deals.map(d => (
                <span key={d.deal_id} style={{ ...styles.promotedTag, color: "#be185d" }}>
                  → {d.deal_type === "event" ? "Event" : "Sponsorship"} (E&amp;S)
                </span>
              ))}
              {!playingPath && deals.length === 0 && (
                <span style={styles.promotedTag}>→ {ALL_DESTINATIONS.find(p => p.value === lead.promoted_to_pipeline)?.label || lead.promoted_to_pipeline}</span>
              )}
              {playingPath && (
                <button
                  style={{ ...styles.btnAction, fontSize: 11, padding: "3px 8px", color: "#7c3aed", borderColor: "#7c3aed" }}
                  onClick={() => onReroute(lead)}
                >
                  Re-route
                </button>
              )}
              {canAdd && (
                <button
                  style={{ ...styles.btnAction, fontSize: 11, padding: "3px 8px" }}
                  title={playingPath ? "Add an event or sponsorship deal" : "Add a membership path or another deal"}
                  onClick={() => onPromote(lead)}
                >
                  + Add
                </button>
              )}
            </div>
          );
        })()}
        {lead.status === "invalid" && (
          <span style={styles.invalidTag}>Invalid</span>
        )}
      </div>
    </div>
  );
}

export default function InboundLeadsModule() {
  const { getAccessTokenSilently } = useAuth0();

  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState("new");
  const [marketFilter, setMarketFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [contactLead, setContactLead] = useState(null);
  const [promoteLead, setPromoteLead] = useState(null);
  const [rerouteLead, setRerouteLead] = useState(null);
  const [dismissLead, setDismissLead] = useState(null);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAccessTokenSilently().then(setToken).catch(() => {});
  }, [getAccessTokenSilently]);

  const fetchOverview = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/inbound/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setOverview(await res.json());
    } catch (e) {}
  }, [token]);

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ status: activeTab, limit: 50, offset: 0 });
      if (marketFilter !== "all") params.set("market", marketFilter);

      const res = await fetch(`${API_BASE}/api/inbound/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load leads");
      const data = await res.json();
      setLeads(data.leads || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError("Failed to load leads. Check your connection.");
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, marketFilter]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  function handleSaved() {
    fetchLeads();
    fetchOverview();
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Inbound Leads</h2>
          <p style={styles.subtitle}>Form submissions and activation uploads</p>
        </div>
      </div>

      <KpiBar overview={overview} />

      <div style={styles.toolbar}>
        <div style={styles.tabs}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              style={{
                ...styles.tab,
                ...(activeTab === tab ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {overview?.total?.[tab] > 0 && (
                <span style={{
                  ...styles.tabCount,
                  ...(activeTab === tab ? styles.tabCountActive : {}),
                }}>
                  {overview.total[tab]}
                </span>
              )}
            </button>
          ))}
        </div>

        <div style={styles.marketFilter}>
          {["all", "TX", "WI"].map(m => (
            <button
              key={m}
              style={{
                ...styles.marketBtn,
                ...(marketFilter === m ? styles.marketBtnActive : {}),
              }}
              onClick={() => setMarketFilter(m)}
            >
              {m === "all" ? "All Markets" : m}
            </button>
          ))}
        </div>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {loading ? (
        <div style={styles.loadingMsg}>Loading leads...</div>
      ) : leads.length === 0 ? (
        <div style={styles.emptyMsg}>
          No {activeTab} leads{marketFilter !== "all" ? ` in ${marketFilter}` : ""}.
        </div>
      ) : (
        <>
          <div style={styles.resultCount}>{total} lead{total !== 1 ? "s" : ""}</div>
          <div style={styles.cardList}>
            {leads.map(lead => (
              <LeadCard
                key={lead.lead_id}
                lead={lead}
                onContact={setContactLead}
                onPromote={setPromoteLead}
                onDismiss={setDismissLead}
                onReroute={setRerouteLead}
              />
            ))}
          </div>
        </>
      )}

      {contactLead && (
        <ContactModal
          lead={contactLead}
          token={token}
          onClose={() => setContactLead(null)}
          onSaved={handleSaved}
        />
      )}
      {promoteLead && (
        <PromoteModal
          lead={promoteLead}
          token={token}
          onClose={() => setPromoteLead(null)}
          onSaved={handleSaved}
        />
      )}
      {rerouteLead && (
        <RerouteModal
          lead={rerouteLead}
          token={token}
          onClose={() => setRerouteLead(null)}
          onSaved={handleSaved}
        />
      )}
      {dismissLead && (
        <DismissModal
          lead={dismissLead}
          token={token}
          onClose={() => setDismissLead(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

const styles = {
  container: { padding: "24px", maxWidth: 1000, margin: "0 auto", fontFamily: "Inter, system-ui, sans-serif" },
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 },
  subtitle: { fontSize: 13, color: "#6b7280", margin: "4px 0 0" },

  kpiBar: {
    background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "16px 20px", marginBottom: 20, display: "flex",
    flexWrap: "wrap", gap: 16, alignItems: "center",
  },
  kpiLoading: { color: "#9ca3af", fontSize: 13 },
  kpiGroup: { display: "flex", gap: 20 },
  kpiCard: { textAlign: "center", minWidth: 70 },
  kpiValue: { fontSize: 26, fontWeight: 700, color: "#111827", lineHeight: 1 },
  kpiLabel: { fontSize: 11, color: "#6b7280", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.04em" },
  kpiMarkets: { display: "flex", gap: 10, flexWrap: "wrap" },
  kpiMarketChip: {
    borderRadius: 8, padding: "6px 12px", display: "flex", alignItems: "center", gap: 8,
  },
  kpiMarketLabel: { fontWeight: 700, fontSize: 13 },
  kpiMarketStat: { fontSize: 12, color: "#374151" },

  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 },
  tabs: { display: "flex", gap: 4 },
  tab: {
    padding: "7px 14px", borderRadius: 6, border: "1px solid #e5e7eb",
    background: "white", fontSize: 13, cursor: "pointer", color: "#374151",
    display: "flex", alignItems: "center", gap: 6, fontWeight: 500,
  },
  tabActive: { background: "#111827", color: "white", borderColor: "#111827" },
  tabCount: {
    background: "#e5e7eb", color: "#374151", borderRadius: 10,
    padding: "1px 6px", fontSize: 11, fontWeight: 700,
  },
  tabCountActive: { background: "#374151", color: "white" },

  marketFilter: { display: "flex", gap: 4 },
  marketBtn: {
    padding: "6px 12px", borderRadius: 6, border: "1px solid #e5e7eb",
    background: "white", fontSize: 12, cursor: "pointer", color: "#6b7280", fontWeight: 500,
  },
  marketBtnActive: { background: "#2563eb", color: "white", borderColor: "#2563eb" },

  resultCount: { fontSize: 12, color: "#9ca3af", marginBottom: 10 },
  cardList: { display: "flex", flexDirection: "column", gap: 10 },
  loadingMsg: { textAlign: "center", color: "#9ca3af", padding: 40 },
  emptyMsg: {
    textAlign: "center", color: "#6b7280", padding: 48,
    border: "1px dashed #e5e7eb", borderRadius: 10,
  },
  errorBanner: {
    background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
    color: "#dc2626", padding: "10px 14px", marginBottom: 16, fontSize: 13,
  },

  card: {
    background: "white", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "14px 18px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 6 },
  cardName: { fontWeight: 600, fontSize: 15, color: "#111827", display: "flex", alignItems: "center", gap: 8 },
  matchBadge: { fontSize: 11, color: "#15803d", fontWeight: 600 },
  cardBadges: { display: "flex", gap: 5, flexWrap: "wrap" },
  badge: {
    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 12,
    letterSpacing: "0.02em",
  },
  cardContact: { display: "flex", gap: 16, marginBottom: 6, flexWrap: "wrap" },
  cardDetail: { fontSize: 13, color: "#374151" },
  cardUtm: { display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" },
  utmTag: {
    fontSize: 11, background: "#f3f4f6", color: "#6b7280",
    borderRadius: 4, padding: "2px 6px",
  },
  cardNotes: { fontSize: 12, color: "#6b7280", fontStyle: "italic", marginBottom: 8 },
  cardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, flexWrap: "wrap", gap: 8 },
  cardTimestamp: { fontSize: 11, color: "#9ca3af" },
  cardActions: { display: "flex", gap: 6 },
  btnAction: {
    padding: "5px 12px", borderRadius: 6, border: "1px solid #d1d5db",
    background: "white", fontSize: 12, cursor: "pointer", fontWeight: 500, color: "#374151",
  },
  btnActionPromote: { background: "#2563eb", color: "white", border: "1px solid #2563eb" },
  btnActionDismiss: { color: "#dc2626", borderColor: "#fca5a5" },
  promotedTag: { fontSize: 12, color: "#15803d", fontWeight: 600 },
  invalidTag: { fontSize: 12, color: "#9ca3af" },

  // Modals
  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modal: {
    background: "white", borderRadius: 12, width: "100%", maxWidth: 460,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden",
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 20px", borderBottom: "1px solid #e5e7eb",
  },
  modalTitle: { fontWeight: 600, fontSize: 15, color: "#111827" },
  modalClose: { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#6b7280", padding: 4 },
  modalBody: { padding: "20px" },
  modalHint: { fontSize: 13, color: "#6b7280", marginBottom: 16, marginTop: 0 },
  modalFooter: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "14px 20px", borderTop: "1px solid #e5e7eb",
  },
  fieldGroup: { marginBottom: 16 },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" },
  select: { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14, color: "#111827" },
  input: { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14, color: "#111827", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14, color: "#111827", resize: "vertical", boxSizing: "border-box" },
  radioRow: { display: "flex", gap: 16 },
  radioLabel: { fontSize: 14, color: "#374151", display: "flex", alignItems: "center", cursor: "pointer" },
  errorMsg: { fontSize: 13, color: "#dc2626", marginTop: 8 },
  pipelineOptions: { display: "flex", gap: 10 },
  pipelineOption: {
    flex: 1, padding: "12px", border: "1px solid #e5e7eb", borderRadius: 8,
    cursor: "pointer", textAlign: "center", transition: "all 0.1s",
  },
  pipelineOptionSelected: { border: "2px solid #2563eb", background: "#eff6ff" },
  pipelineOptionLabel: { fontSize: 13, fontWeight: 600, color: "#374151" },

  btnPrimary: {
    padding: "8px 18px", background: "#111827", color: "white", border: "none",
    borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  btnSecondary: {
    padding: "8px 18px", background: "white", color: "#374151",
    border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
};
